import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { CustomIcon } from '../../../../UI';
import loadService from '../../../../../services/loadService';
import './SendFilesModal.scss';

function getDocKey(doc) {
  if (!doc) return null;
  if (typeof doc === 'string') return doc.replace(/^\/api\/files\//, '').replace(/^\/files\//, '');
  return doc.key || doc.url || null;
}

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

const SendFilesModal = ({ isOpen, onClose, loadId }) => {
  const { watch, getValues } = useFormContext();
  const bolDocuments = watch('bolDocuments') || [];
  const rateConfirmationDocuments = watch('rateConfirmationDocuments') || [];

  const [emailInput, setEmailInput] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState('');

  const hasBOL = Array.isArray(bolDocuments) && bolDocuments.length > 0;
  const hasRateConf = Array.isArray(rateConfirmationDocuments) && rateConfirmationDocuments.length > 0;

  useEffect(() => {
    if (!isOpen) return;
    setEmailInput('');
    setRecipients([]);
    setSendError('');
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      return () => document.body.classList.remove('modal-open');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const addEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      setSendError('Please enter a valid email address.');
      return;
    }
    if (recipients.some((r) => r.email.toLowerCase() === email.toLowerCase())) {
      setSendError('This email is already in the list.');
      return;
    }
    setSendError('');
    setRecipients((prev) => [...prev, { id: Date.now(), email, includeBOL: false, includeRateConfirmation: false }]);
    setEmailInput('');
  };

  const removeEmail = (id) => {
    setRecipients((prev) => prev.filter((r) => r.id !== id));
    setSendError('');
  };

  const setRecipientOption = (id, field, value) => {
    setRecipients((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const handleSend = async () => {
    setSendError('');
    if (recipients.length === 0) {
      setSendError('Add at least one recipient.');
      return;
    }

    const withSelection = recipients.filter((r) => r.includeBOL || r.includeRateConfirmation);
    if (withSelection.length === 0) {
      setSendError('For each recipient, select at least one of BOL or Rate Confirmation.');
      return;
    }

    const bolKeys = (bolDocuments || []).map(getDocKey).filter(Boolean);
    const rateConfKeys = (rateConfirmationDocuments || []).map(getDocKey).filter(Boolean);

    if (withSelection.some((r) => r.includeBOL) && bolKeys.length === 0) {
      setSendError('No BOL documents available. Generate or upload BOL first.');
      return;
    }
    if (withSelection.some((r) => r.includeRateConfirmation) && rateConfKeys.length === 0) {
      setSendError('No Rate Confirmation documents available. Generate or upload first.');
      return;
    }

    setSendLoading(true);
    try {
      const payload = {
        recipients: recipients.map((r) => ({
          email: r.email,
          includeBOL: r.includeBOL,
          includeRateConfirmation: r.includeRateConfirmation,
        })),
        attachmentKeys: {
          bol: bolKeys,
          rateConfirmation: rateConfKeys,
        },
      };
      const apiPayload = loadId ? { loadId, ...payload } : { ...payload, loadData: getValues() };
      await loadService.sendFiles(apiPayload);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to send files.';
      setSendError(msg);
    } finally {
      setSendLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="send-files-modal-overlay" onClick={handleOverlayClick}>
      <div className="send-files-modal" onClick={(e) => e.stopPropagation()}>
        <div className="send-files-modal__header">
          <h2 className="send-files-modal__title">
            <CustomIcon name="file-attach-02" size={24} />
            Send files
          </h2>
          <button type="button" className="send-files-modal__close" onClick={onClose} aria-label="Close">
            <CustomIcon name="close" size={20} />
          </button>
        </div>

        <div className="send-files-modal__body">
          <div className="send-files-modal__field">
            <label className="send-files-modal__label">Add recipient</label>
            <div className="send-files-modal__add-row">
              <input
                type="email"
                className="send-files-modal__input"
                placeholder="Enter email..."
                value={emailInput}
                onChange={(e) => setSendError('') || setEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
              />
              <button type="button" className="send-files-modal__add-btn" onClick={addEmail} disabled={!emailInput.trim()}>
                Add
              </button>
            </div>
          </div>

          {recipients.length > 0 && (
            <div className="send-files-modal__field">
              <span className="send-files-modal__label">Recipients — choose BOL / Rate Confirmation per email</span>
              <ul className="send-files-modal__list">
                {recipients.map((r) => (
                  <li key={r.id} className="send-files-modal__item">
                    <span className="send-files-modal__item-email">{r.email}</span>
                    <div className="send-files-modal__item-checkboxes">
                      <label className="send-files-modal__checkbox-label">
                        <input
                          type="checkbox"
                          checked={r.includeBOL}
                          onChange={(e) => setRecipientOption(r.id, 'includeBOL', e.target.checked)}
                          disabled={!hasBOL}
                        />
                        <span>BOL</span>
                      </label>
                      <label className="send-files-modal__checkbox-label">
                        <input
                          type="checkbox"
                          checked={r.includeRateConfirmation}
                          onChange={(e) => setRecipientOption(r.id, 'includeRateConfirmation', e.target.checked)}
                          disabled={!hasRateConf}
                        />
                        <span>Rate Confirmation</span>
                      </label>
                    </div>
                    <button
                      type="button"
                      className="send-files-modal__remove-btn"
                      onClick={() => removeEmail(r.id)}
                      aria-label="Remove"
                    >
                      <CustomIcon name="close-small" size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sendError && <div className="send-files-modal__error">{sendError}</div>}
        </div>

        <div className="send-files-modal__footer">
          <button type="button" className="send-files-modal__btn send-files-modal__btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="send-files-modal__btn send-files-modal__btn--primary"
            onClick={handleSend}
            disabled={sendLoading || recipients.length === 0}
          >
            {sendLoading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendFilesModal;
